import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '../services/auth';
import { toast } from 'react-toastify';

/**
 * AuthModal - Full authentication modal with Login, Register, and Forgot Password views.
 *
 * Features:
 * - Three views: login, register, forgot password
 * - Client-side form validation (required, email format, password match, min length)
 * - Loading states on submit buttons
 * - Inline error message display
 * - Social login placeholders (Google, GitHub)
 * - Close button (X), backdrop click to close, Escape key to close
 * - Focus trap for keyboard accessibility
 * - Animated transitions between views
 */

// ---------- helpers ----------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(view, fields) {
  const errors = {};

  if (view === 'register' && !fields.name?.trim()) {
    errors.name = 'Full name is required';
  }

  if (!fields.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_RE.test(fields.email)) {
    errors.email = 'Enter a valid email address';
  }

  if (view !== 'forgot') {
    if (!fields.password) {
      errors.password = 'Password is required';
    } else if (fields.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
  }

  if (view === 'register') {
    if (!fields.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (fields.password !== fields.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
  }

  return errors;
}

// ---------- sub-components ----------

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function FocusTrap({ children, active }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!active || !ref.current) return;

    const trap = ref.current;
    const focusable = () => Array.from(trap.querySelectorAll(FOCUSABLE));

    // Focus the first input when the modal opens
    const firstInput = trap.querySelector('input');
    if (firstInput) {
      // Small delay so animation can start
      requestAnimationFrame(() => firstInput.focus());
    }

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const els = focusable();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [active]);

  return <div ref={ref}>{children}</div>;
}

// ---------- styles ----------

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '10px',
  color: 'white',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const inputErrorStyle = {
  ...inputStyle,
  borderColor: 'rgba(239, 68, 68, 0.7)',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: 'rgba(255,255,255,0.8)',
  fontSize: '0.9rem',
  fontWeight: '500',
};

const fieldErrorStyle = {
  color: '#ef4444',
  fontSize: '0.8rem',
  marginTop: '6px',
};

const socialBtnStyle = {
  flex: 1,
  padding: '12px 16px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '10px',
  color: 'rgba(255,255,255,0.9)',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.2s',
};

// ---------- main component ----------

const AuthModal = ({ isOpen, onClose, onSuccess, onDemo, initialView = 'login' }) => {
  const [view, setView] = useState(initialView);
  const [fields, setFields] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Reset state when the modal opens or the view changes from outside
  useEffect(() => {
    if (isOpen) {
      setView(initialView);
      setErrors({});
      setServerError('');
      setLoading(false);
      setForgotSent(false);
    }
  }, [isOpen, initialView]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleFieldChange = useCallback((field) => (e) => {
    setFields((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear field error on change
    setErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
    setServerError('');
  }, []);

  const switchView = useCallback((newView) => {
    setView(newView);
    setErrors({});
    setServerError('');
    setForgotSent(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const validationErrors = validate(view, fields);

    if (view !== 'forgot' && !acceptedTerms) {
      validationErrors.terms = 'You must accept the Terms of Service';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setServerError('');
    setLoading(true);

    try {
      if (view === 'forgot') {
        // Simulate forgot password API call
        await new Promise((resolve) => setTimeout(resolve, 1200));
        setForgotSent(true);
        toast.success('Password reset email sent! Check your inbox.');
      } else if (view === 'login') {
        const result = await authService.login(fields.email, fields.password);
        if (result && (result.access_token || result.token)) {
          toast.success('Welcome back!');
          if (onSuccess) onSuccess(result);
        }
      } else {
        // Register
        const result = await authService.signup({
          email: fields.email,
          password: fields.password,
          name: fields.name,
          company: fields.company,
        });
        if (result && (result.access_token || result.token)) {
          toast.success('Account created successfully!');
          if (onSuccess) onSuccess(result);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      setServerError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // ---------- View titles ----------
  const titles = {
    login: 'Welcome back! Sign in to continue.',
    register: 'Create your account to get started.',
    forgot: 'Reset your password.',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="auth-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label="Authentication"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(10, 10, 15, 0.92)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
          }}
        >
          <FocusTrap active={isOpen}>
            <motion.div
              key="auth-card"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.25 }}
              style={{
                width: '100%',
                maxWidth: '440px',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '40px',
                background: 'rgba(10, 10, 15, 0.97)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '24px',
                boxShadow: '0 25px 80px rgba(0,0,0,0.6), 0 0 40px rgba(102,126,234,0.1)',
                position: 'relative',
              }}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                aria-label="Close authentication modal"
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                }}
              >
                <span aria-hidden="true">&times;</span>
              </button>

              {/* Logo */}
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div
                  style={{
                    fontSize: '1.75rem',
                    fontWeight: 'bold',
                    fontFamily: 'Orbitron, monospace',
                    background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '8px',
                  }}
                >
                  QuantrolixAI
                </div>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>
                  {titles[view]}
                </p>
              </div>

              {/* Tab toggle - only for login / register */}
              {view !== 'forgot' && (
                <div
                  style={{
                    display: 'flex',
                    marginBottom: '28px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    padding: '4px',
                  }}
                  role="tablist"
                  aria-label="Authentication mode"
                >
                  {['login', 'register'].map((v) => (
                    <button
                      key={v}
                      type="button"
                      role="tab"
                      aria-selected={view === v}
                      onClick={() => switchView(v)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: view === v ? 'linear-gradient(135deg, #667eea, #00d4ff)' : 'transparent',
                        border: 'none',
                        borderRadius: '10px',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {v === 'login' ? 'Sign In' : 'Sign Up'}
                    </button>
                  ))}
                </div>
              )}

              {/* Back link for forgot view */}
              {view === 'forgot' && (
                <button
                  type="button"
                  onClick={() => switchView('login')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    marginBottom: '20px',
                    padding: 0,
                  }}
                >
                  <span aria-hidden="true">&larr;</span> Back to Sign In
                </button>
              )}

              {/* Server error */}
              {serverError && (
                <div
                  role="alert"
                  style={{
                    padding: '12px 16px',
                    marginBottom: '20px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '10px',
                    color: '#ef4444',
                    fontSize: '0.9rem',
                  }}
                >
                  {serverError}
                </div>
              )}

              {/* Forgot password success */}
              {view === 'forgot' && forgotSent && (
                <div
                  role="status"
                  style={{
                    padding: '20px',
                    textAlign: 'center',
                    background: 'rgba(34, 197, 94, 0.08)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '12px',
                    marginBottom: '20px',
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>
                    <span aria-hidden="true">&#9993;</span>
                  </div>
                  <p style={{ color: '#22c55e', fontWeight: '600', marginBottom: '6px' }}>Check your email</p>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    We sent a password reset link to <strong style={{ color: 'white' }}>{fields.email}</strong>
                  </p>
                  <button
                    type="button"
                    onClick={() => switchView('login')}
                    style={{
                      marginTop: '16px',
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Back to Sign In
                  </button>
                </div>
              )}

              {/* Form */}
              {!(view === 'forgot' && forgotSent) && (
                <form onSubmit={handleSubmit} noValidate>
                  {/* Name (register only) */}
                  {view === 'register' && (
                    <div style={{ marginBottom: '18px' }}>
                      <label htmlFor="auth-name" style={labelStyle}>Full Name</label>
                      <input
                        id="auth-name"
                        type="text"
                        value={fields.name}
                        onChange={handleFieldChange('name')}
                        placeholder="Jane Doe"
                        autoComplete="name"
                        style={errors.name ? inputErrorStyle : inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(102, 126, 234, 0.8)'; e.target.style.boxShadow = '0 0 0 3px rgba(102,126,234,0.15)'; }}
                        onBlur={(e) => { e.target.style.borderColor = errors.name ? 'rgba(239, 68, 68, 0.7)' : 'rgba(102, 126, 234, 0.3)'; e.target.style.boxShadow = 'none'; }}
                      />
                      {errors.name && <div style={fieldErrorStyle}>{errors.name}</div>}
                    </div>
                  )}

                  {/* Company (register only) */}
                  {view === 'register' && (
                    <div style={{ marginBottom: '18px' }}>
                      <label htmlFor="auth-company" style={labelStyle}>Company <span style={{ color: 'rgba(255,255,255,0.4)' }}>(Optional)</span></label>
                      <input
                        id="auth-company"
                        type="text"
                        value={fields.company}
                        onChange={handleFieldChange('company')}
                        placeholder="Acme Inc."
                        autoComplete="organization"
                        style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(102, 126, 234, 0.8)'; e.target.style.boxShadow = '0 0 0 3px rgba(102,126,234,0.15)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  )}

                  {/* Email */}
                  <div style={{ marginBottom: '18px' }}>
                    <label htmlFor="auth-email" style={labelStyle}>Email Address</label>
                    <input
                      id="auth-email"
                      type="email"
                      value={fields.email}
                      onChange={handleFieldChange('email')}
                      placeholder="you@example.com"
                      autoComplete="email"
                      data-testid="auth-email"
                      style={errors.email ? inputErrorStyle : inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(102, 126, 234, 0.8)'; e.target.style.boxShadow = '0 0 0 3px rgba(102,126,234,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = errors.email ? 'rgba(239, 68, 68, 0.7)' : 'rgba(102, 126, 234, 0.3)'; e.target.style.boxShadow = 'none'; }}
                    />
                    {errors.email && <div style={fieldErrorStyle}>{errors.email}</div>}
                  </div>

                  {/* Password (login & register) */}
                  {view !== 'forgot' && (
                    <div style={{ marginBottom: '18px' }}>
                      <label htmlFor="auth-password" style={labelStyle}>Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="auth-password"
                          type={showPassword ? 'text' : 'password'}
                          value={fields.password}
                          onChange={handleFieldChange('password')}
                          placeholder={view === 'register' ? 'Min. 8 characters' : 'Enter your password'}
                          autoComplete={view === 'login' ? 'current-password' : 'new-password'}
                          data-testid="auth-password"
                          style={{ ...(errors.password ? inputErrorStyle : inputStyle), paddingRight: '50px' }}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(102, 126, 234, 0.8)'; e.target.style.boxShadow = '0 0 0 3px rgba(102,126,234,0.15)'; }}
                          onBlur={(e) => { e.target.style.borderColor = errors.password ? 'rgba(239, 68, 68, 0.7)' : 'rgba(102, 126, 234, 0.3)'; e.target.style.boxShadow = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: 'rgba(255,255,255,0.4)',
                            cursor: 'pointer',
                            padding: '4px',
                            fontSize: '0.85rem',
                          }}
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {errors.password && <div style={fieldErrorStyle}>{errors.password}</div>}

                      {/* Forgot password link (login only) */}
                      {view === 'login' && (
                        <button
                          type="button"
                          onClick={() => switchView('forgot')}
                          style={{
                            display: 'block',
                            marginLeft: 'auto',
                            marginTop: '8px',
                            background: 'none',
                            border: 'none',
                            color: '#667eea',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            padding: 0,
                            textDecoration: 'none',
                          }}
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                  )}

                  {/* Confirm password (register only) */}
                  {view === 'register' && (
                    <div style={{ marginBottom: '18px' }}>
                      <label htmlFor="auth-confirm-password" style={labelStyle}>Confirm Password</label>
                      <input
                        id="auth-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        value={fields.confirmPassword}
                        onChange={handleFieldChange('confirmPassword')}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        data-testid="auth-confirm-password"
                        style={errors.confirmPassword ? inputErrorStyle : inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(102, 126, 234, 0.8)'; e.target.style.boxShadow = '0 0 0 3px rgba(102,126,234,0.15)'; }}
                        onBlur={(e) => { e.target.style.borderColor = errors.confirmPassword ? 'rgba(239, 68, 68, 0.7)' : 'rgba(102, 126, 234, 0.3)'; e.target.style.boxShadow = 'none'; }}
                      />
                      {errors.confirmPassword && <div style={fieldErrorStyle}>{errors.confirmPassword}</div>}
                    </div>
                  )}

                  {/* Terms checkbox (login & register) */}
                  {view !== 'forgot' && (
                    <div style={{ marginBottom: '22px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <input
                        type="checkbox"
                        id="auth-terms"
                        checked={acceptedTerms}
                        onChange={(e) => {
                          setAcceptedTerms(e.target.checked);
                          if (e.target.checked) {
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next.terms;
                              return next;
                            });
                          }
                        }}
                        data-testid="auth-terms"
                        style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#667eea' }}
                      />
                      <label
                        htmlFor="auth-terms"
                        style={{ fontSize: '0.85rem', color: errors.terms ? '#ef4444' : 'rgba(255,255,255,0.7)', lineHeight: '1.5', cursor: 'pointer' }}
                      >
                        I agree to the{' '}
                        <a href="/legal" style={{ color: '#00d4ff', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/legal#privacy" style={{ color: '#00d4ff', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="auth-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: loading
                        ? 'rgba(102, 126, 234, 0.3)'
                        : 'linear-gradient(135deg, #667eea, #00d4ff)',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      boxShadow: loading ? 'none' : '0 4px 20px rgba(102, 126, 234, 0.4)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {loading && (
                      <span
                        className="loading-spinner"
                        style={{ width: '18px', height: '18px' }}
                        aria-hidden="true"
                      />
                    )}
                    {loading
                      ? 'Please wait...'
                      : view === 'login'
                        ? 'Sign In'
                        : view === 'register'
                          ? 'Create Account'
                          : 'Send Reset Link'}
                  </button>
                </form>
              )}

              {/* Divider + Social + Demo (not on forgot view) */}
              {view !== 'forgot' && (
                <>
                  {/* Divider */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', margin: '24px 0', gap: '16px' }}
                    role="separator"
                    aria-hidden="true"
                  >
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>or continue with</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
                  </div>

                  {/* Social login buttons */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <button
                      type="button"
                      onClick={() => toast.info('Google sign-in coming soon!')}
                      aria-label="Sign in with Google"
                      style={socialBtnStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Google
                    </button>
                    <button
                      type="button"
                      onClick={() => toast.info('GitHub sign-in coming soon!')}
                      aria-label="Sign in with GitHub"
                      style={socialBtnStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                      </svg>
                      GitHub
                    </button>
                  </div>

                  {/* Demo button */}
                  {onDemo && (
                    <button
                      type="button"
                      onClick={onDemo}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        borderRadius: '12px',
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.4)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    >
                      <span aria-hidden="true">&#9654;</span> Explore Interactive Demo
                    </button>
                  )}
                </>
              )}
            </motion.div>
          </FocusTrap>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
